import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { SITE_ID } from '../lib/constants';
import { mockCategories } from '../mocks/categories';

export interface Category {
  id: string;
  name: string;
  description: string;
  image_url: string;
  slug: string;
  product_count: number;
  ust_kategori_id: string | null;
  sira: number;
}

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchCategories() {
      setLoading(true);
      try {
        const { data } = await supabase
          .from('urun_kategorileri')
          .select('id, kategori_adi, aciklama, gorsel_url, ust_kategori_id, sira')
          .eq('site_id', SITE_ID)
          .eq('aktif', true)
          .order('sira', { ascending: true });

        if (cancelled) return;

        if (data && data.length > 0) {
          // Get product counts for each category
          const counts: Record<string, number> = {};
          try {
            const { data: products } = await supabase
              .from('urunler')
              .select('kategori')
              .eq('site_id', SITE_ID)
              .eq('aktif', true);

            if (products) {
              products.forEach(p => {
                if (p.kategori) {
                  counts[p.kategori] = (counts[p.kategori] || 0) + 1;
                }
              });
            }
          } catch {
            // ignore count errors
          }

          const mapped: Category[] = data.map(c => ({
            id: c.id as string,
            name: c.kategori_adi as string,
            description: (c.aciklama as string) || '',
            image_url: (c.gorsel_url as string) || '',
            slug: (c.kategori_adi as string).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
            product_count: counts[c.kategori_adi as string] || 0,
            ust_kategori_id: (c.ust_kategori_id as string) || null,
            sira: (c.sira as number) || 0,
          }));

          setCategories(mapped);
        } else {
          // Fallback to mock categories
          setCategories(
            mockCategories.map(c => ({
              id: c.id,
              name: c.name,
              description: c.description || '',
              image_url: c.image_url,
              slug: c.slug,
              product_count: c.product_count || 0,
              ust_kategori_id: null,
              sira: 0,
            }))
          );
        }
      } catch {
        if (!cancelled) {
          setCategories(
            mockCategories.map(c => ({
              id: c.id,
              name: c.name,
              description: c.description || '',
              image_url: c.image_url,
              slug: c.slug,
              product_count: c.product_count || 0,
              ust_kategori_id: null,
              sira: 0,
            }))
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchCategories();
    return () => { cancelled = true; };
  }, []);

  return { categories, loading };
}
